/**
 * Super Admin editor for tasks whose agent_id is null.
 * Homeowner names and contact details never belong in this payload.
 */

const PROPERTY_TYPE_OPTIONS = [
  { id: 'single_family', label: 'Single Family Home' },
  { id: 'condo', label: 'Condo' },
  { id: 'townhome', label: 'Townhome' },
  { id: 'mobile_home', label: 'Mobile Home' },
];

const MATCH_LISTS = ['construction', 'exterior', 'foundation', 'roof'];

function canWriteGlobalTasks(profile) {
  return Boolean(profile && profile.role === 'super_admin');
}

function blankMatch() {
  return {
    construction: [],
    exterior: [],
    foundation: [],
    roof: [],
    hvacIndex: '',
    hvacMin: '',
    hvacMax: '',
    roofAgeMin: '',
    roofAgeMax: '',
    whenRoofAgeUnknown: false,
    match: '',
    key: '',
    extra: {},
  };
}

function asList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || '').trim()).filter(Boolean);
}

function matchFromConditions(conditions) {
  const form = blankMatch();
  if (!conditions || typeof conditions !== 'object' || Array.isArray(conditions)) return form;
  const extra = {};
  Object.keys(conditions).forEach((key) => {
    if (MATCH_LISTS.indexOf(key) !== -1) {
      form[key] = asList(conditions[key]);
      return;
    }
    if (key === 'hvac_index') form.hvacIndex = conditions[key] == null ? '' : String(conditions[key]);
    else if (key === 'hvac_min') form.hvacMin = conditions[key] == null ? '' : String(conditions[key]);
    else if (key === 'hvac_max') form.hvacMax = conditions[key] == null ? '' : String(conditions[key]);
    else if (key === 'roof_age_min') form.roofAgeMin = conditions[key] == null ? '' : String(conditions[key]);
    else if (key === 'roof_age_max') form.roofAgeMax = conditions[key] == null ? '' : String(conditions[key]);
    else if (key === 'when_roof_age_unknown') form.whenRoofAgeUnknown = conditions[key] === true;
    else if (key === 'match') form.match = conditions[key] == null ? '' : String(conditions[key]);
    else if (key === 'key') form.key = conditions[key] == null ? '' : String(conditions[key]);
    else extra[key] = conditions[key];
  });
  form.extra = extra;
  return form;
}

function numberOrSkip(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

function conditionsFromMatch(form) {
  const source = form || blankMatch();
  const out = {};
  const extra = source.extra && typeof source.extra === 'object' ? source.extra : {};
  Object.keys(extra).forEach((key) => {
    out[key] = extra[key];
  });
  MATCH_LISTS.forEach((key) => {
    const list = asList(source[key]);
    if (list.length) out[key] = list;
  });
  const numbers = [
    ['hvac_index', source.hvacIndex],
    ['hvac_min', source.hvacMin],
    ['hvac_max', source.hvacMax],
    ['roof_age_min', source.roofAgeMin],
    ['roof_age_max', source.roofAgeMax],
  ];
  numbers.forEach(([key, value]) => {
    const parsed = numberOrSkip(value);
    if (parsed != null) out[key] = parsed;
  });
  if (source.whenRoofAgeUnknown) out.when_roof_age_unknown = true;
  if (source.match === 'any' || source.match === 'all') out.match = source.match;
  const key = String(source.key || '').trim();
  if (key) out.key = key;
  return Object.keys(out).length ? out : null;
}

function taskToForm(task) {
  const row = task || {};
  return {
    title: row.title || '',
    description: row.description || '',
    category: row.category || '',
    type: row.type === 'financial' ? 'financial' : 'maintenance',
    frequency: row.frequency || '',
    priority: row.priority || 'medium',
    active: row.active !== false,
    image_url: row.image_url || '',
    diy_tips: row.diy_tips || '',
    youtube_video_url: row.youtube_video_url || '',
    difficulty_level: row.difficulty_level || '',
    estimated_time: row.estimated_time || '',
    when_to_call_pro: row.when_to_call_pro || '',
    property_types: asList(row.property_types),
    required_feature: row.required_feature || '',
    month_to_perform: row.month_to_perform || '',
    match: matchFromConditions(row.conditions),
  };
}

function acceptableVideoLink(value) {
  const raw = String(value || '').trim();
  if (!raw) return true;
  return /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/)|youtu\.be\/)[^&\s?/]+/i.test(raw);
}

function formToRecord(form) {
  const source = form || {};
  const title = String(source.title || '').trim();
  if (!title) return { error: 'Add a title' };
  const category = String(source.category || '').trim();
  if (!category) return { error: 'Choose a category' };
  const frequency = String(source.frequency || '').trim();
  if (!frequency) return { error: 'Add a frequency' };
  const youtube = String(source.youtube_video_url || '').trim();
  if (!acceptableVideoLink(youtube)) return { error: 'Enter a YouTube link, or leave it blank' };
  const type = source.type === 'financial' ? 'financial' : 'maintenance';
  const priority = ['low', 'medium', 'high'].indexOf(source.priority) === -1 ? 'medium' : source.priority;
  const types = asList(source.property_types);
  const difficulty = String(source.difficulty_level || '').trim();
  return {
    record: {
      title,
      description: String(source.description || '').trim(),
      category,
      type,
      frequency,
      priority,
      active: source.active !== false,
      image_url: String(source.image_url || '').trim() || null,
      diy_tips: String(source.diy_tips || '').trim() || null,
      youtube_video_url: youtube || null,
      difficulty_level: difficulty || null,
      estimated_time: String(source.estimated_time || '').trim() || null,
      when_to_call_pro: String(source.when_to_call_pro || '').trim() || null,
      property_types: types.length ? types : null,
      required_feature: String(source.required_feature || '').trim() || null,
      month_to_perform: String(source.month_to_perform || '').trim() || null,
      conditions: conditionsFromMatch(source.match),
      agent_id: null,
      is_global: true,
    },
  };
}

function linkedTaskMessage(count) {
  const total = Number(count) || 0;
  if (total === 1) return '1 homeowner task uses this. Saving changes that task.';
  return `${total} homeowner tasks use this. Saving changes those tasks.`;
}

function deletePrompt(count) {
  const total = Number(count) || 0;
  if (total <= 0) {
    return {
      needsDeactivate: false,
      title: 'Delete this task?',
      message: 'No homeowner tasks use it.',
      confirmLabel: 'Delete',
    };
  }
  const noun = total === 1 ? 'homeowner task uses' : 'homeowner tasks use';
  return {
    needsDeactivate: true,
    title: 'This task is in use',
    message: `${total} ${noun} this. Deactivate it so it is not added to new homes. Those tasks stay on the homes.`,
    confirmLabel: 'Deactivate',
    deleteMessage: `${total} ${noun} this. Deleting unlinks them. Those tasks stay on the homes, and they lose this template's instructions. This cannot be undone.`,
  };
}

module.exports = {
  PROPERTY_TYPE_OPTIONS,
  canWriteGlobalTasks,
  blankMatch,
  matchFromConditions,
  conditionsFromMatch,
  taskToForm,
  formToRecord,
  linkedTaskMessage,
  deletePrompt,
};
